type KeyEasing = 'linear' | 'easeIn' | 'easeOut' | 'easeInOut' | 'step';

const KEY_EASING_OPTIONS: readonly KeyEasing[] = ['linear', 'easeIn', 'easeOut', 'easeInOut', 'step'];

const KEY_EASING_LABELS: Record<KeyEasing, string> = {
    linear: 'Linear',
    easeIn: 'Ease In',
    easeOut: 'Ease Out',
    easeInOut: 'Ease In-Out',
    step: 'Step (Hold)'
};

/**
 * Interface for animation tracks that can be attached to animatable targets.
 * Each track owns its keyframes and handles capture, interpolation, and evaluation.
 */
interface AnimTrack {
    /** Array of frame numbers where keyframes exist */
    readonly keys: readonly number[];

    /**
     * Add a keyframe at the specified frame, capturing current state.
     * If a key already exists at this frame, it will be updated.
     * @returns true if the track was modified, false if the operation was a no-op.
     */
    addKey(frame: number): boolean;

    /**
     * Remove the keyframe at the specified frame.
     * @returns true if the track was modified, false if the operation was a no-op.
     */
    removeKey(frame: number): boolean;

    /**
     * Move a keyframe from one frame to another.
     * @returns true if the track was modified, false if the operation was a no-op.
     */
    moveKey(fromFrame: number, toFrame: number): boolean;

    /**
     * Copy a keyframe from one frame to another.
     * The original keyframe remains in place.
     * @returns true if the track was modified, false if the operation was a no-op.
     */
    copyKey(fromFrame: number, toFrame: number): boolean;

    /**
     * Get the easing type for a keyframe (affects the outgoing segment).
     * Returns undefined if not set (uses default smooth interpolation).
     */
    getKeyEasing(frame: number): KeyEasing | undefined;

    /**
     * Set the easing type for a keyframe (affects the outgoing segment).
     * @returns true if the track was modified.
     */
    setKeyEasing(frame: number, easing: KeyEasing | undefined): boolean;

    /**
     * Clear all keyframes.
     */
    clear(): void;

    /**
     * Return a deep copy of the track's internal state (for undo snapshots).
     */
    snapshot(): unknown;

    /**
     * Replace the track's internal state with a previously captured snapshot
     * and fire appropriate change events.
     */
    restore(snapshot: unknown): void;
}

export { AnimTrack, KEY_EASING_LABELS, KEY_EASING_OPTIONS };
export type { KeyEasing };
